
import { getCurrentPlatform } from '@/utils/platformUtils';

/**
 * Storage keys with version prefix for better maintenance
 */
const STORAGE_KEYS = {
  REFERRAL_CODE: 'ref_v1_code',
  REFERRAL_DATA: 'ref_v1_data',
  VISITOR_ID: 'ref_v1_visitor',
  REFERRAL_EXPIRES_AT: 'ref_v1_expires'
};

/**
 * Default TTL for referral codes (30 days in milliseconds)
 */
const DEFAULT_REFERRAL_TTL = 30 * 24 * 60 * 60 * 1000;

/**
 * ReferralData interface to enforce type safety
 */
export interface ReferralData {
  code: string;
  campaign?: string;
  visitor_id: string;
  stored_at: string;
  expires_at: string;
  platform: string;
}

/**
 * A service class to handle all referral storage operations with proper error handling
 */
export class ReferralStorageService {
  /**
   * Store referral code with error handling and fallbacks
   */
  static storeReferralCode(
    code: string, 
    campaign?: string,
    ttl: number = DEFAULT_REFERRAL_TTL
  ): boolean {
    if (!code) return false;

    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + ttl);
      const visitorId = this.getOrCreateVisitorId();
      
      // Create referral data object
      const referralData: ReferralData = {
        code,
        campaign,
        visitor_id: visitorId,
        stored_at: now.toISOString(),
        expires_at: expiresAt.toISOString(),
        platform: getCurrentPlatform()
      };
      
      // Try to store in local storage first
      let localSuccess = false;
      try {
        localStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, code);
        localStorage.setItem(STORAGE_KEYS.REFERRAL_DATA, JSON.stringify(referralData));
        localStorage.setItem(STORAGE_KEYS.REFERRAL_EXPIRES_AT, expiresAt.toISOString());
        localSuccess = true;
      } catch (e) {
        console.warn('Failed to save referral to localStorage:', e);
      }
      
      // Try session storage as fallback
      let sessionSuccess = false;
      try {
        sessionStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, code);
        sessionStorage.setItem(STORAGE_KEYS.REFERRAL_DATA, JSON.stringify(referralData));
        sessionStorage.setItem(STORAGE_KEYS.REFERRAL_EXPIRES_AT, expiresAt.toISOString());
        sessionSuccess = true;
      } catch (e) {
        console.warn('Failed to save referral to sessionStorage:', e);
      }
      
      if (localSuccess || sessionSuccess) {
        console.log(`Referral code stored successfully: ${code}${campaign ? ` (campaign: ${campaign})` : ''}`);
        return true;
      } else {
        console.error('Failed to store referral code in any storage mechanism');
        return false;
      }
    } catch (error: unknown) {
      console.error(
        'Critical error storing referral code:',
        error instanceof Error ? error.message : error
      );
      return false;
    }
  }

  /**
   * Get stored referral code with validation and expiration check
   */
  static getStoredReferralCode(): string | null {
    try {
      // Try sessionStorage first
      let code = this.getFromStorage(STORAGE_KEYS.REFERRAL_CODE, 'session');
      let expiresAt = this.getFromStorage(STORAGE_KEYS.REFERRAL_EXPIRES_AT, 'session');
      
      // Fall back to localStorage if not in sessionStorage
      if (!code || !expiresAt) {
        code = this.getFromStorage(STORAGE_KEYS.REFERRAL_CODE, 'local');
        expiresAt = this.getFromStorage(STORAGE_KEYS.REFERRAL_EXPIRES_AT, 'local');
      }
      
      // Check if the code has expired
      if (code && expiresAt) {
        const now = new Date();
        const expiration = new Date(expiresAt);
        
        if (now > expiration) {
          // Code has expired, clean up storage
          this.clearReferralData();
          return null;
        }
      }
      
      return code;
    } catch (error: unknown) {
      console.error(
        'Error retrieving referral code:',
        error instanceof Error ? error.message : error
      );
      return null;
    }
  }

  /**
   * Get full referral data object from storage
   */
  static getStoredReferralData(): ReferralData | null {
    try {
      // Try both storage types
      let data: ReferralData | null = null;
      
      // Check session storage first
      try {
        const sessionData = sessionStorage.getItem(STORAGE_KEYS.REFERRAL_DATA);
        if (sessionData) {
          data = JSON.parse(sessionData);
        }
      } catch (e) {
        console.warn('Failed to get referral data from sessionStorage:', e);
      }
      
      // Fall back to local storage
      if (!data) {
        try {
          const localData = localStorage.getItem(STORAGE_KEYS.REFERRAL_DATA);
          if (localData) {
            data = JSON.parse(localData);
          }
        } catch (e) {
          console.warn('Failed to get referral data from localStorage:', e);
        }
      }
      
      // Check expiration
      if (data && data.expires_at) {
        const now = new Date();
        const expiration = new Date(data.expires_at);
        
        if (now > expiration) {
          this.clearReferralData();
          return null;
        }
      }
      
      return data;
    } catch (error: unknown) {
      console.error(
        'Error retrieving referral data:',
        error instanceof Error ? error.message : error
      );
      return null;
    }
  }

  /**
   * Clear all referral data from storage
   */
  static clearReferralData(): void {
    try {
      // Clear from localStorage
      try {
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_DATA);
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_EXPIRES_AT);
      } catch (e) {
        console.warn('Error clearing from localStorage:', e);
      }
      
      // Clear from sessionStorage
      try {
        sessionStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
        sessionStorage.removeItem(STORAGE_KEYS.REFERRAL_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.REFERRAL_EXPIRES_AT);
      } catch (e) {
        console.warn('Error clearing from sessionStorage:', e);
      }
      
      console.log('Referral data cleared from storage');
    } catch (error: unknown) {
      console.error(
        'Error clearing referral data:',
        error instanceof Error ? error.message : error
      );
    }
  }

  /**
   * Get or create a persistent visitor ID
   */
  static getOrCreateVisitorId(): string {
    try {
      // Try to get existing visitor ID from localStorage for persistence
      let visitorId = this.getFromStorage(STORAGE_KEYS.VISITOR_ID, 'local');
      
      // Fall back to sessionStorage if needed
      if (!visitorId) {
        visitorId = this.getFromStorage(STORAGE_KEYS.VISITOR_ID, 'session');
      }
      
      // Create new ID if none exists
      if (!visitorId) {
        visitorId = `v-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
        
        // Store in both storage mechanisms
        try {
          localStorage.setItem(STORAGE_KEYS.VISITOR_ID, visitorId);
        } catch (e) {
          console.error('Failed to store visitor id', e);
        }
        try {
          sessionStorage.setItem(STORAGE_KEYS.VISITOR_ID, visitorId);
        } catch (e) {
          console.error('Failed to store visitor id', e);
        }
      }
      
      return visitorId;
    } catch (error: unknown) {
      // Fallback to a basic ID if storage is unavailable
      console.error(
        'Error managing visitor ID:',
        error instanceof Error ? error.message : error
      );
      return `v-${Date.now().toString(36)}`;
    }
  }

  /**
   * Helper function to get data from a specific storage type with error handling
   */
  private static getFromStorage(key: string, storageType: 'local' | 'session'): string | null {
    try {
      const storage = storageType === 'local' ? localStorage : sessionStorage;
      return storage.getItem(key);
    } catch (e) {
      console.warn(`Error reading from ${storageType} storage:`, e);
      return null;
    }
  }
}

// Export constants for use in other modules
export { STORAGE_KEYS, DEFAULT_REFERRAL_TTL };
